import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Card, CardBody } from "reactstrap";
// import Loader from "Components/Common/Loader";
import TableContainer from "Components/Common/TableContainer";
import CustomButton from "Components/Custom/CustomButton";
import ActionMenu from "Components/Common/ActionMenu";
import { useAppDispatch } from "Components/Hooks/hooks";
import { createSelector } from "reselect";
import { selectSubCategoriesState } from "slices/subcategories/reducer";
import { useSelector } from "react-redux";
import {
  deleteSubCategory,
  getSubCategories,
} from "slices/subcategories/thunk";
import DeleteItemModal from "Components/Modals/DeleteItemModal";

interface SubCategoryTableProps {
  isLoadingData?: boolean;
  onAddSubCategory: () => void;
  onEditSubCategory: (subCat: any) => void;
}

const SubcategoriesTable = ({
  onAddSubCategory,
  onEditSubCategory,
}: SubCategoryTableProps) => {
  const dispatch = useAppDispatch();
  const [deleteCatId, setDeleteCatId] = useState<number | null>(null);

  const categorySelector = createSelector(
    selectSubCategoriesState,
    (state) => ({
      subcategories: state.subcategories,
      isLoading: state.status.fetch,
      error: state.errors.fetch,
      meta: state.pagination,
    }),
  );

  const { subcategories, isLoading, meta } = useSelector(categorySelector);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    const params = searchTerm ? { search: searchTerm } : { page: currentPage };
    dispatch(getSubCategories(params));
  }, [currentPage, dispatch, searchTerm]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "SUBCATEGORY",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue() ? cell.getValue() : "-"}
            </span>
          );
        },
      },
      {
        header: "CATEGORY",
        accessorKey: "category.name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "ITEMS",
        accessorKey: "products_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ?? 0}
          </span>
        ),
      },
      {
        header: "ACTION",
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <ActionMenu
              onEdit={() => onEditSubCategory(cell?.row?.original)}
              onDelete={() => setDeleteCatId(cell.row.original.id)}
            />
          );
        },
      },
    ],
    [onEditSubCategory],
  );

  return (
    <React.Fragment>
      <DeleteItemModal
        show={deleteCatId !== null}
        onCloseClick={() => setDeleteCatId(null)}
        onDeleteClick={() => {
          if (deleteCatId) {
            dispatch(deleteSubCategory(deleteCatId));
          }
          setDeleteCatId(null);
        }}
      />
      <Card id="membersTable" className="br-10 bg-white">
        <CardBody className="pt-0">
          <TableContainer
            columns={columns}
            data={subcategories}
            isGlobalFilter={true}
            divClass="table-responsive table-card mb-2 bg-white"
            tableClass="align-middle table-nowrap table-borderless category-table"
            thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
            theadClass="table-light"
            SearchPlaceholder="Search subcategory"
            onSearch={handleSearch}
            onPageChange={handlePageChange}
            customPageSize={meta?.per_page}
            totalPages={meta?.last_page}
            currentPage={meta?.current_page}
            fromPage={meta?.from}
            toPage={meta?.to}
            totalItems={meta?.total}
            searchAlign="left"
            rightContent={
              <CustomButton
                text="New Subcategory"
                leftIcon={<i className="ri-add-line me-1 align-bottom fs-5" />}
                className="bg-primary br-7"
                onClick={onAddSubCategory}
              />
            }
            isInitialLoading={isLoading === "loading" && !subcategories?.length}
            isPaginationLoading={
              isLoading === "loading" && subcategories?.length > 0
            }
          />
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default SubcategoriesTable;
